import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import DesafioShareCard from './DesafioShareCard';

const IconShare = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function ShareDesafioModal({ desafio, conquista, onFechar }) {
  const cardRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState(false);

  // Gera a imagem ao abrir o modal (após fontes carregadas)
  useEffect(() => {
    let cancelled = false;

    const gerar = async () => {
      setGerando(true);
      setErro(false);

      // Aguarda fontes carregarem para evitar fallback de fonte do browser
      try { await document.fonts.ready; } catch {}

      // Pequeno delay para garantir que o card off-screen foi pintado
      await new Promise(r => setTimeout(r, 120));

      if (cancelled || !cardRef.current) { setGerando(false); return; }

      try {
        const scale = Math.min(window.devicePixelRatio || 2, 2);
        const canvas = await html2canvas(cardRef.current, {
          scale,
          useCORS: true,
          allowTaint: true,
          // Fundo sólido (cor inicial do gradiente) — evita falha em alguns browsers
          backgroundColor: '#2f2b5f',
          logging: false,
          // Desativa foreignObject — mais compatível com Android/Firefox
          foreignObjectRendering: false,
        });

        if (!cancelled) {
          setPreviewUrl(canvas.toDataURL('image/png'));
        }
      } catch (err) {
        console.error('[ShareDesafioModal] html2canvas error:', err);
        if (!cancelled) setErro(true);
      } finally {
        if (!cancelled) setGerando(false);
      }
    };

    gerar();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = async () => {
    if (!previewUrl) return;
    try {
      const res = await fetch(previewUrl);
      const blob = await res.blob();
      const file = new File([blob], 'conquista-clube-do-eu.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Minha conquista — Clube do Eu' });
        return;
      }
      // Fallback: download direto
      handleDownload();
    } catch {
      handleDownload();
    }
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.download = 'conquista-clube-do-eu.png';
    link.href = previewUrl;
    link.click();
  };

  const handleCompartilharTexto = async () => {
    const texto = `Completei o desafio "${desafio?.desafio_nome}" — Clube do Eu ✨`;
    try {
      if (navigator.canShare && navigator.canShare()) {
        await navigator.share({ text: texto, title: 'Minha conquista — Clube do Eu' });
      } else {
        await navigator.clipboard.writeText(texto);
        alert('Texto copiado para a área de transferência!');
      }
    } catch {
      // silencioso
    }
  };

  return (
    <>
      {/* Card off-screen para captura — wrapper com overflow:hidden para funcionar em todos os browsers */}
      <div
        style={{
          position: 'absolute',
          overflow: 'hidden',
          width: 0,
          height: 0,
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <DesafioShareCard
          desafio={desafio}
          parametroAntes={desafio?.parametro_antes}
          parametroDepois={desafio?.parametro_depois}
          conquista={conquista}
          cardRef={cardRef}
          offscreen={false}
        />
      </div>

      {/* Preview overlay */}
      {previewUrl && (
        <div className="oracle-preview-backdrop" onClick={onFechar}>
          <div className="oracle-preview-box" onClick={e => e.stopPropagation()}>
            <img src={previewUrl} alt="Card para compartilhar" className="oracle-preview-img" draggable={false} />
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleShare}>
              Compartilhar / Salvar imagem
            </button>
            <button className="btn btn-ghost oracle-preview-close" onClick={onFechar}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal principal */}
      <div className="modal-celebracao-backdrop">
        <div className="modal-celebracao-card">
          <div className="modal-celebracao-decor" />

          <div className="modal-celebracao-texto" style={{ textAlign: 'center' }}>
            <p className="modal-celebracao-supra">Sua conquista</p>
            <h2 className="modal-celebracao-titulo">{desafio?.desafio_nome}</h2>
            <p className="modal-celebracao-mensagem">
              {desafio?.duracao} dias de presença e compromisso com você mesma.
            </p>
          </div>

          <div className="modal-celebracao-acoes">
            {gerando && (
              <button className="btn btn-primary" style={{ width: '100%' }} disabled>
                Gerando imagem...
              </button>
            )}

            {!gerando && previewUrl && (
              <button
                className="btn btn-primary"
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                onClick={handleShare}
              >
                <IconShare />
                Compartilhar conquista
              </button>
            )}

            {!gerando && erro && (
              <>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, textAlign: 'center', opacity: 0.7, marginBottom: 8 }}>
                  Não foi possível gerar a imagem.
                </p>
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  onClick={handleCompartilharTexto}
                >
                  <IconShare />
                  Compartilhar conquista
                </button>
              </>
            )}

            <button className="btn btn-ghost" style={{ width: '100%' }} onClick={onFechar}>
              Fechar
            </button>
          </div>

          <button className="modal-celebracao-fechar" onClick={onFechar} aria-label="Fechar">
            <IconClose />
          </button>
        </div>
      </div>
    </>
  );
}
